import React from "react";
import { spring, useCurrentFrame, useVideoConfig } from "remotion";
import { LOOK } from "../look";

type Props = {
  text: string;
  /** Local frame at which the first word starts revealing. */
  start?: number;
  /** Frames between two words (3 = brisk, 5 = comfortable). */
  stagger?: number;
  fontSize: number;
  color?: string;
  weight?: number;
  /** Word index → color override, for the one word that matters. */
  accents?: Record<number, string>;
  lineHeight?: number;
  letterSpacing?: number;
  align?: React.CSSProperties["textAlign"];
  style?: React.CSSProperties;
};

/**
 * Kinetic headline: each word rises out of its own mask, staggered. The
 * mask (overflow hidden) keeps the motion graphic and legible; nothing
 * floats in from nowhere. Driven by `spring()` so it settles on the frame,
 * and sized in px because the canvas is fixed at 1080.
 */
export const Words: React.FC<Props> = ({
  text,
  start = 0,
  stagger = 3,
  fontSize,
  color = LOOK.ink,
  weight = LOOK.bold,
  accents,
  lineHeight = 1.05,
  letterSpacing,
  align = "center",
  style,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const words = text.split(" ");

  return (
    <div
      style={{
        fontFamily: LOOK.display,
        fontWeight: weight,
        fontSize,
        lineHeight,
        letterSpacing: letterSpacing ?? -fontSize * 0.035,
        color,
        textAlign: align,
        textWrap: "balance",
        ...style,
      }}
    >
      {words.map((word, i) => {
        const p = spring({
          frame: frame - start - i * stagger,
          fps,
          config: { damping: 20, stiffness: 190, mass: 0.7 },
        });
        return (
          <React.Fragment key={i}>
          <span
            style={{
              display: "inline-block",
              overflow: "hidden",
              // Room for descenders + accents inside the mask.
              padding: "0.08em 0.02em 0.14em",
              margin: "-0.08em 0 -0.14em",
              verticalAlign: "top",
            }}
          >
            <span
              style={{
                display: "inline-block",
                translate: `0 ${(1 - p) * 140}%`,
                rotate: `${(1 - p) * 4}deg`,
                opacity: p > 0.001 ? 1 : 0,
                color: accents?.[i] ?? color,
              }}
            >
              {word}
            </span>
          </span>
          {i < words.length - 1 ? " " : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};
